// Client side of the optional Ably push upgrade. Everything here degrades to
// "do nothing" when the server reports Ably disabled, so the room screen simply
// keeps polling. The SDK is dynamically imported so it's only downloaded when
// the feature is actually on.
import type { RoomStateView } from "@shared/types";

interface TokenResponse {
  enabled: boolean;
  tokenRequest?: unknown;
}

async function fetchToken(): Promise<TokenResponse> {
  try {
    const r = await fetch("/api/ably-token", { method: "POST" });
    if (!r.ok) return { enabled: false };
    return (await r.json()) as TokenResponse;
  } catch {
    return { enabled: false };
  }
}

export interface RoomChannel {
  close(): void;
}

/** Subscribe to a room's realtime channel when Ably is configured server-side.
 *  Returns null (caller falls back to polling) when it's disabled or can't
 *  connect. Pushed snapshots have the same shape as the room-state poll. */
export async function connectRoomChannel(
  code: string,
  onState: (state: RoomStateView) => void,
): Promise<RoomChannel | null> {
  const probe = await fetchToken();
  if (!probe.enabled) return null;

  try {
    // Lazy-load the SDK so it's only fetched when the feature is enabled.
    const Ably: any = await import("ably");
    const realtime = new Ably.Realtime({
      authCallback: (
        _params: unknown,
        cb: (err: unknown, token: unknown) => void,
      ) => {
        fetchToken()
          .then((res) =>
            res.enabled && res.tokenRequest
              ? cb(null, res.tokenRequest)
              : cb(new Error("Ably disabled"), null),
          )
          .catch((e) => cb(e, null));
      },
    });

    const channel = realtime.channels.get(`room:${code}`);
    channel.subscribe("state", (msg: { data: unknown }) => {
      onState(msg.data as RoomStateView);
    });

    return {
      close() {
        try {
          channel.unsubscribe();
          realtime.close();
        } catch {
          /* ignore */
        }
      },
    };
  } catch {
    return null;
  }
}
